import { useCallback, useContext, useEffect, useState } from 'react';

import { TOAST_STATUS } from '@evinced-private/ui-common';

import {
	ILocalEvToastProps,
	NotificationsContext
} from 'src/providers/notificationsProvider/NotificationsContext';
import { getReleaseNotes } from 'src/services/ReleaseNotesService';
import { ReleaseNote } from 'src/types/release-notes/ReleaseNote';
import { ReleaseNotesProduct } from 'src/types/release-notes/ReleaseNotesProduct';

import { releaseNotesCache } from '../services/ReleaseNotesCache';

interface UseReleaseNotesDataProps {
	currentProduct: ReleaseNotesProduct | null;
}

interface UseReleaseNotesDataReturn {
	releaseNotes: ReleaseNote[];
	releaseNotesLoading: boolean;
	releaseNotesError: boolean;
	isLoadingMore: boolean;
	hasMoreReleaseNotes: boolean;
	loadMoreReleaseNotes: () => Promise<void>;
	retryReleaseNotes: () => void;
}

// Simple fetch helper
const fetchReleaseNotesPage = async (
	productId: number,
	page: number
): Promise<{ data: ReleaseNote[]; isComplete: boolean }> => {
	const response = await getReleaseNotes(productId, page);
	const { data } = response;

	let isComplete = false;
	if (response.meta?.pagination) {
		const { pageCount } = response.meta.pagination;
		isComplete = page >= pageCount;
	} else {
		isComplete = true;
	}

	return { data, isComplete };
};

// Shared error handling helper
const handleReleaseNotesError = (
	toast: ((toastDetails: ILocalEvToastProps) => void) | null,
	setReleaseNotes: (notes: ReleaseNote[]) => void,
	setError: (error: boolean) => void
): void => {
	if (toast) {
		toast({
			announcement: 'Something went wrong',
			status: TOAST_STATUS.FAIL,
			show: true
		});
	}
	setReleaseNotes([]);
	setError(true);
};

export const useReleaseNotesData = ({
	currentProduct
}: UseReleaseNotesDataProps): UseReleaseNotesDataReturn => {
	const { toast } = useContext(NotificationsContext);

	const [releaseNotes, setReleaseNotes] = useState<ReleaseNote[]>([]);
	const [isInitialLoading, setIsInitialLoading] = useState(false);
	const [releaseNotesError, setReleaseNotesError] = useState(false);
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const [currentPage, setCurrentPage] = useState(1);

	const cachedEntry = currentProduct ? releaseNotesCache.get(currentProduct.id) : null;
	const needsInitialFetch = currentProduct && !cachedEntry && !releaseNotesError;
	const releaseNotesLoading = isInitialLoading || needsInitialFetch;
	const hasMoreReleaseNotes = cachedEntry ? !cachedEntry.isComplete : false;

	const loadMoreReleaseNotes = useCallback(async (): Promise<void> => {
		if (!currentProduct || isLoadingMore) {
			return;
		}

		setIsLoadingMore(true);
		try {
			const { data: newReleaseNotes, isComplete } = await fetchReleaseNotesPage(
				currentProduct.id,
				currentPage + 1
			);

			const allReleaseNotes = [...releaseNotes, ...newReleaseNotes];

			releaseNotesCache.set(currentProduct.id, allReleaseNotes, isComplete, currentPage + 1);

			setReleaseNotes(allReleaseNotes);
			setCurrentPage(currentPage + 1);
		} catch {
			handleReleaseNotesError(toast, setReleaseNotes, setReleaseNotesError);
		} finally {
			setIsLoadingMore(false);
		}
	}, [currentProduct, currentPage, releaseNotes, isLoadingMore, toast]);

	const retryReleaseNotes = useCallback(async (): Promise<void> => {
		if (!currentProduct) {
			return;
		}

		setIsInitialLoading(true);
		setReleaseNotesError(false);
		setCurrentPage(1);

		try {
			const { data, isComplete } = await fetchReleaseNotesPage(currentProduct.id, 1);

			releaseNotesCache.set(currentProduct.id, data, isComplete, 1);

			setReleaseNotes(data);
		} catch {
			handleReleaseNotesError(toast, setReleaseNotes, setReleaseNotesError);
		} finally {
			setIsInitialLoading(false);
		}
	}, [currentProduct, toast]);

	// Load release notes on product change (use cache if available)
	useEffect(() => {
		if (!currentProduct) {
			setReleaseNotes([]);
			setCurrentPage(1);
			setReleaseNotesError(false);
			return;
		}

		const cached = releaseNotesCache.get(currentProduct.id);

		if (cached) {
			setReleaseNotes(cached.data);
			setCurrentPage(cached.lastPage);
			setReleaseNotesError(false);
			return;
		}

		const fetchInitialData = async (): Promise<void> => {
			setIsInitialLoading(true);
			setReleaseNotesError(false);
			setCurrentPage(1);

			try {
				const { data, isComplete } = await fetchReleaseNotesPage(currentProduct.id, 1);

				releaseNotesCache.set(currentProduct.id, data, isComplete, 1);

				setReleaseNotes(data);
			} catch {
				handleReleaseNotesError(toast, setReleaseNotes, setReleaseNotesError);
			} finally {
				setIsInitialLoading(false);
			}
		};

		fetchInitialData();
	}, [currentProduct, toast]);

	return {
		releaseNotes,
		releaseNotesLoading,
		releaseNotesError,
		isLoadingMore,
		hasMoreReleaseNotes,
		loadMoreReleaseNotes,
		retryReleaseNotes
	};
};
